import { Injectable, NgZone } from "@angular/core";
import { LoaderService } from "./loader.service";
import { BehaviorSubject, Observable } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { StoreService } from "./store.service";
import { ToastrService } from "ngx-toastr";

@Injectable({
    providedIn: "root",
})
export class LocationService {
    constructor(
        public loaderService: LoaderService,
        public dialog: MatDialog,
        public storeService: StoreService,
        public toastr: ToastrService,
        private ngZone: NgZone
    ) {}

    private tracking = false;

    // Stores latitude/longitude (sent as API headers) and keeps them updated.
    // Shows the browser's location prompt if the user has not answered it yet, so call it only
    // after the user agreed (the /auth/permission toggle) or once access is already granted.
    startTracking(): void {
        if (this.tracking) return;
        this.tracking = true;
        this.watchPosition().subscribe({
            next: (position) => {
                this.storeService.set({ key: "latitude", value: position.coords.latitude.toString() });
                this.storeService.set({ key: "longitude", value: position.coords.longitude.toString() });
            },
            error: (err) => {
                this.tracking = false;
                console.error(err);
            },
        });
    }

    // App start: resume tracking without prompting. Only when the browser has already
    // granted location, or (no Permissions API) the user already accepted on /auth/permission.
    async resumeTrackingIfAllowed(): Promise<void> {
        try {
            if (navigator.permissions?.query) {
                const result = await navigator.permissions.query({ name: "geolocation" as PermissionName });
                if (result.state === "granted") this.startTracking();
                return;
            }
        } catch {}
        if (this.storeService.get("permission") === "true") this.startTracking();
    }

    // True only when the browser has already granted location. False when it has not, and where
    // the Permissions API is missing (it cannot be checked without showing the prompt).
    async isLocationGranted(): Promise<boolean> {
        try {
            const result = await navigator.permissions?.query({ name: "geolocation" as PermissionName });
            return result?.state === "granted";
        } catch {
            return false;
        }
    }

    // Calls onChange with the browser's location permission now and whenever it changes, e.g. when
    // a user who blocked location picks "Always allow" from the address-bar icon. Returns a function
    // that stops listening. Does nothing where the Permissions API is missing. onChange runs inside
    // the Angular zone, so the page re-renders.
    watchPermissionState(onChange: (state: PermissionState) => void): () => void {
        let status: PermissionStatus | null = null;
        let stopped = false;
        const listener = () => status && this.ngZone.run(() => onChange(status!.state));
        navigator.permissions
            ?.query({ name: "geolocation" as PermissionName })
            .then((result) => {
                if (stopped) return;
                status = result;
                this.ngZone.run(() => onChange(result.state));
                result.addEventListener("change", listener);
            })
            .catch(() => {});
        return () => {
            stopped = true;
            status?.removeEventListener("change", listener);
        };
    }

    // Opens the browser's location prompt and resolves as soon as the user answers it. Waiting for
    // getCurrentPosition() alone is slow: it reports only once a position fix arrives, seconds after
    // "Allow". The permission change event fires on the click itself; whichever comes first wins.
    requestPermission(): Promise<boolean> {
        return new Promise((resolve) => {
            if (!navigator.geolocation) return resolve(false);

            let status: PermissionStatus | null = null;
            let done = false;
            const finish = (granted: boolean) => {
                if (done) return;
                done = true;
                if (status) status.onchange = null;
                this.ngZone.run(() => resolve(granted));
            };

            // Opens the prompt. Also the only answer on browsers without the Permissions API: any
            // error other than PERMISSION_DENIED (timeout, no fix) still means access was granted.
            navigator.geolocation.getCurrentPosition(
                () => finish(true),
                (error) => finish(error.code !== error.PERMISSION_DENIED)
            );

            navigator.permissions
                ?.query({ name: "geolocation" as PermissionName })
                .then((result) => {
                    status = result;
                    if (result.state === "granted") return finish(true);
                    result.onchange = () => {
                        if (result.state === "granted") finish(true);
                        else if (result.state === "denied") finish(false);
                    };
                })
                .catch(() => {});
        });
    }

    captureLocationInBackground(): void {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    // console.log("position.coords.latitude", position.coords.latitude);
                    // console.log("position.coords.longitude", position.coords.longitude);

                    this.storeService.set({
                        key: "latitude",
                        value: position.coords.latitude.toString(),
                    });
                    this.storeService.set({
                        key: "longitude",
                        value: position.coords.longitude.toString(),
                    });
                },
                (error) => {
                    console.error("Geolocation error:", error);
                }
            );
        } else {
            console.error("Geolocation not supported");
        }
    }

    async checkPermission(): Promise<boolean> {
        try {
            // Step 1: Use Permissions API if available
            console.log("navigator.permissions", navigator.permissions);

            if (navigator.permissions) {
                const result = await navigator.permissions.query({ name: "geolocation" as PermissionName });
                console.log("result", result);
                if (result.state === "granted") return true;
                // if (result.state === "denied") return false;
                // if 'prompt', we still need to trigger getCurrentPosition()
            }

            // Step 2: Ask user via geolocation request (prompts user if needed)
            return await new Promise<boolean>((resolve) => {
                console.log("navigator.geolocation", navigator.geolocation);
                if (!navigator.geolocation) {
                    resolve(false);
                    return;
                }

                navigator.geolocation.getCurrentPosition(
                    (res) => {
                        resolve(true);
                        return;
                    },
                    (error) => {
                        resolve(false);
                        return;
                    },
                    { timeout: 10000 }
                );
            });
        } catch (err) {
            return false;
        }
    }

    // watchPosition(): Observable<GeolocationPosition> {
    //     return new Observable((observer) => {
    //         if (!navigator.geolocation) {
    //             observer.error("Geolocation not supported");
    //             return;
    //         }

    //         const watchId = navigator.geolocation.watchPosition(
    //             (position) => {
    //                 // Run inside Angular zone to trigger change detection
    //                 this.ngZone.run(() => observer.next(position));
    //             },
    //             (error) => {
    //                 this.ngZone.run(() => observer.error(error));
    //             },
    //             {
    //                 enableHighAccuracy: false,
    //                 timeout: 30000,
    //                 maximumAge: 10000,
    //             }
    //         );

    //         // Cleanup when unsubscribed
    //         return () => navigator.geolocation.clearWatch(watchId);
    //     });
    // }

    watchPosition(): Observable<GeolocationPosition> {
        return new Observable((observer) => {
            if (!navigator.geolocation) {
                observer.error("Geolocation not supported");
                return;
            }

            const optionsLow = {
                enableHighAccuracy: false,
                timeout: 15000,
                maximumAge: 5000,
            };

            const optionsHigh = {
                enableHighAccuracy: true,
                timeout: 30000,
                maximumAge: 0,
            };

            // First: fast location
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    console.log("pos", pos);

                    observer.next(pos);

                    // Next: Accurate location (optional)
                    navigator.geolocation.watchPosition(
                        (position) => {
                            // console.log("position 1", position);
                            observer.next(position); // <-- remove comma
                        },
                        (error) => {
                            console.log("error 1", error);
                            observer.error(error);
                        },
                        optionsHigh
                    );
                },
                (error) => {
                    console.log("error 2", error);
                    observer.error(error);
                },
                optionsLow
            );
        });
    }
}
